import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@work4all/components/lib/dataDisplay/card';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { COLUMNS_BY_GENERAL_SETTINGS_TYPE } from '@work4all/models/lib/constants';

import { CONTROLLER_DEFINITIONS } from '../../../../../../utils/lookup-type-mapping';
import { ControllerPlus } from '../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { Collapse, ControlWrapper, Panel } from '../../../components';
import { useMaskContext } from '../../../hooks/mask-context';
import { GeneralSettingsMaskFormValue } from '../types';

export const GeneralTabPanel = () => {
  const { t } = useTranslation();
  const form = useFormContextPlus<GeneralSettingsMaskFormValue>();
  const { register, control } = form;
  const mask = useMaskContext();

  // Get additional columns for the current entity
  const additionalColumns = useMemo(() => {
    return COLUMNS_BY_GENERAL_SETTINGS_TYPE[mask.entity] || [];
  }, [mask.entity]);

  // Generate input field configurations for additional columns
  const additionalFields = useMemo(() => {
    return additionalColumns.map((columnName) => ({
      key: columnName,
      name: columnName,
      label: t(`COMMON.${columnName.toUpperCase()}`),
      ...CONTROLLER_DEFINITIONS[mask.entity][columnName],
    }));
  }, [additionalColumns, mask.entity, t]);

  return (
    <Panel style={{ height: '100%' }}>
      <Collapse defaultOpen title={t('MASK.GENERAL')}>
        <Card>
          <ControlWrapper columns={1}>
            <LabeledInput
              {...register('name')}
              required
              label={t('COMMON.NAME')}
            />
          </ControlWrapper>
          {/* Render additional fields dynamically */}
          <ControlWrapper columns={2}>
            {additionalFields.map((fieldConfig) =>
              fieldConfig.useRegister ? (
                <LabeledInput
                  key={fieldConfig.key}
                  {...register(fieldConfig.name)}
                  label={fieldConfig.label}
                  type={fieldConfig.type}
                />
              ) : (
                <ControllerPlus
                  key={fieldConfig.key}
                  name={fieldConfig.name}
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      {...field}
                      value={field.value as string}
                      label={fieldConfig.label}
                      type={fieldConfig.type}
                      {...fieldConfig.makeProps?.(field, form)}
                    />
                  )}
                />
              )
            )}
          </ControlWrapper>
        </Card>
      </Collapse>
    </Panel>
  );
};
